import React, { useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "/api";

// ─── Design tokens (mirror StrategyBuilder) ─────────────────────────────────
const T = {
  bg: "#eef2f7", card: "#ffffff", border: "#e8edf3",
  text: "#0f172a", sub: "#64748b", muted: "#94a3b8",
  blue: "#2563eb", indigo: "#4f46e5", red: "#ef4444", green: "#10b981", field: "#f8fafc",
};
const inp = { width: "100%", padding: "9px 11px", fontSize: 13, border: `1px solid ${T.border}`, borderRadius: 10, color: T.text, background: T.field, boxSizing: "border-box", outline: "none", fontFamily: "inherit" };

const Card = ({ children, style }) => (
  <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: 22, marginBottom: 16, boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 10px 30px rgba(15,23,42,0.035)", ...style }}>{children}</div>
);
const Head = ({ icon, title, sub }) => (
  <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "flex-start" }}>
    <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#eef2ff,#dbeafe)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, flexShrink: 0 }}>{icon}</div>
    <div>
      <div style={{ fontSize: 15, fontWeight: 800, color: T.text }}>{title}</div>
      {sub && <div style={{ fontSize: 12, color: T.muted, marginTop: 2, lineHeight: 1.5 }}>{sub}</div>}
    </div>
  </div>
);
const Lbl = ({ children }) => <span style={{ display: "block", fontSize: 10.5, fontWeight: 700, color: T.sub, marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.06em" }}>{children}</span>;
const Btn = ({ ch, onClick, v = "ghost", disabled, sm }) => {
  const s = {
    blue: { bg: "linear-gradient(135deg,#4f46e5,#2563eb)", cl: "#fff", bd: "none", sh: "0 6px 16px rgba(37,99,235,0.32)" },
    green: { bg: "linear-gradient(135deg,#10b981,#059669)", cl: "#fff", bd: "none", sh: "0 6px 16px rgba(16,185,129,0.3)" },
    red: { bg: "#fef2f2", cl: "#dc2626", bd: "1px solid #fecaca", sh: "none" },
    ghost: { bg: "#fff", cl: "#475569", bd: "1px solid #e2e8f0", sh: "none" },
  }[v];
  return <button onClick={onClick} disabled={disabled} style={{ background: s.bg, color: s.cl, border: s.bd, borderRadius: 10, padding: sm ? "6px 13px" : "10px 20px", fontSize: sm ? 12 : 13.5, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.65 : 1, whiteSpace: "nowrap", fontFamily: "inherit", boxShadow: s.sh }}>{ch}</button>;
};

const EXAMPLES = [
  "Short NIFTY ATM straddle at 9:20, exit 15:15, 30% SL per leg, move SL to cost at 50% profit.",
  "Sell BANKNIFTY strangle at 0.3 delta CE and PE, 25% SL, re-enter on SL 2 times, exit all legs on SL.",
  "Sell NIFTY ATM straddle 2 lots, trail SL: lock ₹1000 and trail ₹500 for every ₹250 profit, daily MTM loss ₹5000.",
  "Buy NIFTY weekly ATM CE when RSI(14) crosses above 60 on 5min, 20% SL, 40% target.",
  "Wait & trade: enter a NIFTY short straddle only after spot moves up 0.3%, pre-punch SL at 30%.",
];

// ─── Strategy preview helpers ───────────────────────────────────────────────
const fmtPct = (v) => (v || v === 0 ? v : "—");

function legLabel(leg) {
  const action = String(leg.buySell || leg.action || "SELL").toUpperCase();
  const cp = (leg.callPut || leg.optionType || "CE").toUpperCase().includes("P") ? "PE" : "CE";
  let strike;
  const crit = String(leg.strikeCriteria || "").toUpperCase();
  if (crit === "DELTA_NEAR" || (leg.strikeBy === "delta")) strike = "Δ" + (leg.strikeValue != null ? leg.strikeValue : leg.deltaValue);
  else if (crit === "CP") strike = "premium≈" + leg.strikeValue;
  else if (crit === "CP_GTE") strike = "premium≥" + leg.strikeValue;
  else if (crit === "CP_LTE") strike = "premium≤" + leg.strikeValue;
  else if (crit === "ATM_PT") strike = "ATM" + (leg.strikeValue >= 0 ? "+" : "") + leg.strikeValue + "pt";
  else strike = String(leg.strikeType || "ATM").replace("_", " "); // "OTM_250" → "OTM 250"
  return { action, cp, strike };
}

const slText = (type, val) => {
  if (val == null || val === "" || val === 0) return "—";
  const t = String(type || "percent").toLowerCase();
  if (t.includes("point") || t.includes("pt")) return val + " pt";
  if (t.includes("amount")) return "₹" + val;
  return val + "%";
};

// Human-readable list of which advanced features the generated strategy uses.
function advancedFeatures(s) {
  const out = [];
  if (s.moveSLtoCost) out.push(`Move SL to Cost @ ${s.moveSLtoCostAt != null ? s.moveSLtoCostAt : 50}${String(s.moveSLtoCostAtUnit || 'percent') === 'points' ? ' pts' : '%'} profit`);
  if (s.prePunchSL) out.push("Pre-Punch SL (resting broker stop)");
  if (s.exitAllOnSL) out.push("Exit all legs on SL");
  if (s.exitAllOnTarget) out.push("Exit all legs on Target");
  if (s.trailSlType && s.trailSlType !== "disabled") {
    const unit = s.trailSlType === "profit_amount" ? "₹" : s.trailSlType === "profit_points" ? "pt" : "%";
    out.push(`Trail SL — lock ${unit === "₹" ? "₹" + s.lockProfitAt : s.lockProfitAt + unit}, trail ${unit === "₹" ? "₹" + s.trailBy : s.trailBy + unit} per ${unit === "₹" ? "₹" + s.trailStep : s.trailStep + unit}`);
  }
  if (s.waitAndTrade?.enabled) {
    const w = s.waitAndTrade;
    if ((w.type || "time") === "time") out.push(`Wait & Trade — delay ${w.minutes} min`);
    else out.push(`Wait & Trade — underlying ${w.type.replace("pct", "%").replace("pt", "pt").replace("_up", " up").replace("_down", " down")} ${w.value}`);
  }
  if (s.reEntryOnSL?.enabled) out.push(`Re-Entry on SL × ${s.reEntryOnSL.times === 99 ? "∞" : s.reEntryOnSL.times}`);
  if (s.reExecuteOnTarget?.enabled) out.push(`Re-Execute on Target × ${s.reExecuteOnTarget.times}`);
  if (s.premiumDiff?.enabled) out.push(`Premium Difference ≤ ${s.premiumDiff.points} pts`);
  if (s.rollAdjust?.enabled) out.push(`Rolling adjust — roll when ≤ ${s.rollAdjust.triggerStrikes} strikes away, re-sell ${s.rollAdjust.reSellStrikes} out (max ${s.rollAdjust.maxRolls}/side)`);
  if (s.deltaAdjust?.enabled) {
    const d = s.deltaAdjust;
    out.push(`Delta-Neutral Adjust — imbalance ≥ ${d.deltaImbalance}, high-Δ ≥ ${d.highLegDelta}, loss ≥ ${d.lossPremiumPct}% prem, move ≥ ${d.movePoints}pt, ≤ ${d.triggerStrikes} strikes; exit @ ${d.maxLossPremiumMult}× prem / ${d.trendExitPoints}pt trend`);
  }
  if (s.mtmLossLimit?.enabled) out.push(`Daily MTM loss limit ₹${s.mtmLossLimit.amount}`);
  if (s.mtmProfitLock?.enabled) out.push(`Daily MTM profit lock ₹${s.mtmProfitLock.amount}`);
  return out;
}

function condText(c) {
  const op = (o) => {
    if (!o) return "?";
    if (o.type === "Number") return o.value;
    if (o.type === "Candle") return o.price || "close";
    return o.type + (o.period ? `(${o.period})` : "");
  };
  return `${op(c.left)} ${c.comparator || "?"} ${op(c.right)}`;
}

// Plain-language walkthrough of how the generated strategy will actually run.
// Built from the structured strategy (not the prompt), so it always matches what
// gets deployed — and it explains the advanced rules (delta-neutral adjust,
// rolling, trailing, etc.) in full sentences. Returns [{ h, items[] }] sections.
function explainStrategy(s) {
  if (!s) return [];
  const ind = s.indicatorConfig?.enabled ? s.indicatorConfig : null;
  const sections = [];

  // ── Entry ──
  const entry = [];
  entry.push(`Trades ${s.instrument || "NIFTY"} options${s.underlyingType ? ` (${s.underlyingType} as the underlying)` : ""}, ${s.productType || "MIS"} product${s.lots ? `, ${s.lots} lot${s.lots > 1 ? "s" : ""}` : ""}.`);
  if (ind) {
    const conds = (ind.entryConditions || []).map(condText).join(`  ${ind.entryLogic}  `);
    entry.push(`Enters once ${conds || "the entry conditions"} fire on the ${ind.timeframe} chart.`);
  } else {
    entry.push(`Enters at ${s.entryTime || "09:16"} on ${(s.days || []).join(", ") || "all weekdays"}.`);
  }
  if (s.waitAndTrade?.enabled) {
    const w = s.waitAndTrade;
    entry.push((w.type || "time") === "time"
      ? `Wait & Trade: holds off ${w.minutes} min after entry time before placing orders.`
      : `Wait & Trade: enters only after the underlying moves ${w.type.replace("pct", "%").replace("pt", " pt").replace("_up", " up").replace("_down", " down")} ${w.value}.`);
  }
  if (s.premiumDiff?.enabled) entry.push(`Enters only when the two legs' premiums are within ${s.premiumDiff.points} points of each other.`);
  if (s.noTradeAfter) entry.push(`No fresh entry after ${s.noTradeAfter}.`);
  sections.push({ h: "Entry", items: entry });

  // ── Positions ──
  const legs = (s.legs || []).map((leg) => {
    const l = legLabel(leg);
    const sl = slText(leg.slType, leg.slValue != null ? leg.slValue : leg.sl);
    const tp = slText(leg.tpType, leg.targetValue != null ? leg.targetValue : leg.tp);
    const bits = [`${l.action} ${leg.qty || 1} lot ${l.cp} @ ${l.strike}`];
    if (sl !== "—") bits.push(`${sl} SL`);
    if (tp !== "—") bits.push(`${tp} target`);
    return bits.join(" · ");
  });
  sections.push({ h: "Positions", items: legs.length ? legs : ["No legs configured."] });

  // ── Risk & adjustments ──
  const risk = [];
  if (s.overallTargetValue || s.overallSlValue) {
    if (s.overallTargetValue) risk.push(`Books the whole position at ₹${s.overallTargetValue} total profit.`);
    if (s.overallSlValue) risk.push(`Stops the whole position at ₹${s.overallSlValue} total loss.`);
  } else {
    risk.push(`No overall ₹ profit/loss cap — risk is governed by the per-leg SL/target and the rules below.`);
  }
  if (s.deltaAdjust?.enabled) {
    const d = s.deltaAdjust;
    const trg = [];
    if (d.deltaImbalance > 0) trg.push(`the two legs' deltas diverge by ≥ ${d.deltaImbalance}`);
    if (d.highLegDelta > 0) trg.push(`either leg's delta reaches ${d.highLegDelta}`);
    if (d.lossPremiumPct > 0) trg.push(`the loss hits ${d.lossPremiumPct}% of premium collected`);
    if (d.movePoints > 0) trg.push(`the index moves ${d.movePoints} pts since the last entry`);
    if (d.triggerStrikes > 0) trg.push(`spot comes within ${d.triggerStrikes} strikes of a sold leg`);
    risk.push(`Delta-neutral adjust: each cycle it recomputes both legs' live deltas and rebalances — buys back the profit leg and re-sells it at the tested leg's delta — when ${trg.join(", or ")}${d.maxAdjusts ? ` (at most ${d.maxAdjusts} time${d.maxAdjusts > 1 ? "s" : ""})` : ""}.`);
    const exits = [];
    if (d.maxLossPremiumMult > 0) exits.push(`the loss reaches ${d.maxLossPremiumMult}× the premium collected`);
    if (d.trendExitPoints > 0) exits.push(`the index trends ${d.trendExitPoints} pts one way`);
    if (exits.length) risk.push(`Squares off everything if ${exits.join(" or ")}.`);
  }
  if (s.rollAdjust?.enabled) {
    const r = s.rollAdjust;
    risk.push(`Rolling adjust: when spot comes within ${r.triggerStrikes} strikes of a short leg, it buys that leg back and re-sells ${r.reSellStrikes} strikes away${r.maxRolls ? ` (max ${r.maxRolls} rolls per side)` : ""}.`);
  }
  if (s.moveSLtoCost) risk.push(`Moves each leg's SL to break-even once it is ${s.moveSLtoCostAt != null ? s.moveSLtoCostAt : 50}${String(s.moveSLtoCostAtUnit || 'percent') === 'points' ? ' pts' : '%'} in profit.`);
  if (s.prePunchSL) risk.push(`Rests each leg's stop-loss with the broker in advance (Pre-Punch SL).`);
  if (s.exitAllOnSL) risk.push(`Exits all legs the moment any one leg hits its SL.`);
  if (s.exitAllOnTarget) risk.push(`Exits all legs the moment any one leg hits its target.`);
  if (s.trailSlType && s.trailSlType !== "disabled") {
    const u = s.trailSlType === "profit_amount" ? "₹" : s.trailSlType === "profit_points" ? " pt" : "%";
    const f = (v) => (u === "₹" ? "₹" + v : v + u);
    risk.push(`Trails the overall SL — starts locking once profit reaches ${f(s.lockProfitAt)}, then raises the lock by ${f(s.trailBy)} for every ${f(s.trailStep)} of further profit.`);
  }
  if (s.reEntryOnSL?.enabled) risk.push(`Re-enters after a leg SL hit ${s.reEntryOnSL.times === 99 ? "(unlimited)" : `up to ${s.reEntryOnSL.times}×`}.`);
  if (s.reExecuteOnTarget?.enabled) risk.push(`Re-executes after a leg target hit up to ${s.reExecuteOnTarget.times}×.`);
  if (s.mtmLossLimit?.enabled) risk.push(`Daily MTM loss limit: exits all at ₹${s.mtmLossLimit.amount} day loss.`);
  if (s.mtmProfitLock?.enabled) risk.push(`Daily MTM profit lock: exits all at ₹${s.mtmProfitLock.amount} day profit.`);
  sections.push({ h: "Risk & adjustments", items: risk });

  // ── Exit ──
  const ex = [];
  if (ind && (ind.exitConditions || []).length) ex.push(`Exits when ${ind.exitConditions.map(condText).join(`  ${ind.exitLogic}  `)}.`);
  ex.push(`All open positions are squared off by ${s.exitTime || "15:15"}.`);
  sections.push({ h: "Exit", items: ex });

  return sections;
}

// ─── Page ───────────────────────────────────────────────────────────────────
export default function StrategyPrompt() {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ text: "", type: "" });
  const [strategy, setStrategy] = useState(null);
  const [source, setSource] = useState("");
  const [mode, setMode] = useState("Paper");
  const [coverage, setCoverage] = useState("");      // "full" | "partial" | "unknown"
  const [unsupported, setUnsupported] = useState([]); // logic the builder couldn't express

  const token = localStorage.getItem("token");
  const hdr = { Authorization: "Bearer " + token };

  const generate = async () => {
    if (!text.trim()) return setMsg({ text: "Describe your strategy first.", type: "error" });
    setLoading(true);
    setMsg({ text: "", type: "" });
    try {
      const res = await axios.post(API + "/strategies/from-text", { text }, { headers: hdr });
      setStrategy(res.data.strategy);
      setSource(res.data.source);
      setCoverage(res.data.coverage || "");
      setUnsupported(Array.isArray(res.data.unsupported) ? res.data.unsupported : []);
      setMsg({ text: "Strategy generated — review it below, then deploy.", type: "success" });
    } catch (e) {
      setMsg({ text: "Generate failed: " + (e.response?.data?.message || e.message), type: "error" });
    }
    setLoading(false);
  };

  // Create the strategy, then (optionally) deploy it in the chosen mode.
  // AI-generated strategies are gated for LIVE: the backend blocks live deploy
  // until they've been backtested and approved, so a Live request is routed to
  // the backtest page (where the user reviews and approves) instead of deploying
  // straight away. Paper mode deploys directly.
  const submit = async (deploy, openBuilder) => {
    if (!strategy) return;
    setBusy(true);
    setMsg({ text: "", type: "" });
    try {
      const createRes = await axios.post(API + "/strategies", strategy, { headers: hdr });
      const id = createRes.data._id;
      if (openBuilder) { window.location.href = "/strategy-builder?id=" + id; return; }
      if (deploy && mode === "Live") {
        setMsg({ text: "Saved — opening backtest. Review it, then Approve & Deploy Live.", type: "success" });
        setTimeout(() => { window.location.href = "/backtesting?strategy=" + id; }, 700);
        return;
      }
      if (deploy) {
        await axios.post(API + "/strategies/" + id + "/deploy", { mode }, { headers: hdr });
        setMsg({ text: `Deployed in ${mode} mode! Redirecting…`, type: "success" });
      } else {
        setMsg({ text: "Saved as draft! Redirecting…", type: "success" });
      }
      setTimeout(() => { window.location.href = "/strategies"; }, 900);
    } catch (e) {
      setMsg({ text: "Failed: " + (e.response?.data?.message || e.message), type: "error" });
      setBusy(false);
    }
  };

  const feats = strategy ? advancedFeatures(strategy) : [];
  const ind = strategy?.indicatorConfig?.enabled ? strategy.indicatorConfig : null;

  return (
    <div style={{ padding: "20px 24px 40px", fontFamily: "Inter, -apple-system, sans-serif", background: T.bg, minHeight: "100vh" }}>
      <style>{`
        .sp-textarea { transition: border-color .15s, box-shadow .15s, background .15s; }
        .sp-textarea:focus { border-color: #93c5fd; box-shadow: 0 0 0 4px rgba(37,99,235,0.10); background: #fff; }
        .sp-chip { transition: border-color .15s, box-shadow .15s, transform .15s, background .15s; }
        .sp-chip:hover { border-color: #93c5fd; background: #fff; box-shadow: 0 6px 16px rgba(37,99,235,0.12); transform: translateY(-2px); }
        .sp-chip:hover .sp-chip-txt { color: #1e293b; }
      `}</style>

      {/* Hero */}
      <div className="page-hero" style={{ position: "sticky", top: 0, zIndex: 50, overflow: "hidden", background: "linear-gradient(120deg,#4f46e5 0%,#2563eb 55%,#1d4ed8 100%)", borderRadius: 18, padding: "24px 26px", marginBottom: 18, color: "#fff", boxShadow: "0 14px 34px rgba(37,99,235,0.26)" }}>
        <div style={{ position: "absolute", top: -40, right: -30, width: 180, height: 180, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,255,255,0.18), transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -60, right: 90, width: 150, height: 150, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,255,255,0.10), transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "relative" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10.5, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", background: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 999, padding: "4px 11px", marginBottom: 10 }}>✨ AI Strategy Builder</div>
          <h1 style={{ fontSize: 24, fontWeight: 900, margin: 0, letterSpacing: "-0.5px" }}>Create a Strategy with a Prompt</h1>
          <div style={{ fontSize: 13.5, opacity: 0.92, marginTop: 5, maxWidth: 640, lineHeight: 1.5 }}>Describe your idea in plain English — legs, strikes, stop-loss, timing and advanced rules — and we'll turn it into a ready-to-deploy strategy.</div>
        </div>
      </div>

      {msg.text && (
        <div style={{ padding: "11px 16px", borderRadius: 12, marginBottom: 16, fontSize: 13, fontWeight: 600, background: msg.type === "error" ? "#fef2f2" : "#ecfdf5", color: msg.type === "error" ? "#b91c1c" : "#065f46", border: `1px solid ${msg.type === "error" ? "#fecaca" : "#a7f3d0"}` }}>{msg.text}</div>
      )}

      {/* Prompt input */}
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: 22 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 13, marginBottom: 16 }}>
            <div style={{ width: 42, height: 42, borderRadius: 12, background: "linear-gradient(135deg,#6366f1,#2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0, boxShadow: "0 6px 16px rgba(37,99,235,0.28)" }}>✨</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <div style={{ fontSize: 15.5, fontWeight: 800, color: T.text }}>Describe your strategy</div>
                <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.08em", color: T.indigo, background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: 999, padding: "2px 9px", textTransform: "uppercase" }}>AI Powered</span>
              </div>
              <div style={{ fontSize: 12, color: T.muted, marginTop: 3, lineHeight: 1.5 }}>Plain English — instrument, legs, strikes, SL/target, timing and advanced rules (trail SL, re-entry, move SL to cost, pre-punch, wait &amp; trade, premium difference, MTM limits…).</div>
            </div>
          </div>

          <div style={{ position: "relative" }}>
            <textarea className="sp-textarea" value={text} onChange={(e) => setText(e.target.value)} rows={5}
              placeholder="e.g. Sell NIFTY ATM straddle at 9:20, 30% SL per leg, move SL to cost at 50% profit, exit all legs on SL, square off 15:15."
              style={{ ...inp, width: "100%", maxWidth: "100%", resize: "vertical", lineHeight: 1.6, padding: "14px 14px 26px", fontSize: 13.5, borderRadius: 12 }} />
            {text && <span style={{ position: "absolute", bottom: 9, right: 12, fontSize: 10.5, color: T.muted, pointerEvents: "none" }}>{text.length} characters</span>}
          </div>

          <div style={{ marginTop: 16, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Btn ch={loading ? "✨ Generating…" : "✨ Generate Strategy"} v="blue" onClick={generate} disabled={loading} />
            {text && !loading && <Btn ch="Clear text" onClick={() => setText("")} />}
            {strategy && <Btn ch="Reset" onClick={() => { setStrategy(null); setText(""); setMsg({ text: "", type: "" }); setCoverage(""); setUnsupported([]); }} />}
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: 11, color: T.muted, display: "flex", alignItems: "center", gap: 5 }}>💡 Tip: include exact times &amp; % for the best result</span>
          </div>
        </div>

        {/* Example suggestions */}
        <div style={{ borderTop: `1px solid ${T.border}`, background: "#fafbfd", padding: "15px 22px 18px" }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: T.sub, marginBottom: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>⚡ Quick start — tap an example</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(250px, 100%), 1fr))", gap: 9 }}>
            {EXAMPLES.map((ex, i) => (
              <button key={i} className="sp-chip" onClick={() => setText(ex)} style={{ textAlign: "left", display: "flex", gap: 9, alignItems: "flex-start", background: "#fff", border: `1px solid ${T.border}`, borderRadius: 11, padding: "10px 12px", cursor: "pointer", fontFamily: "inherit" }}>
                <span style={{ flexShrink: 0, width: 20, height: 20, borderRadius: 6, background: "#eef2ff", color: T.indigo, fontWeight: 800, fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center" }}>{i + 1}</span>
                <span className="sp-chip-txt" style={{ fontSize: 11.5, lineHeight: 1.45, color: T.sub }}>{ex}</span>
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* Generated preview */}
      {strategy && (
        <Card>
          <Head icon="📋" title="Review & Deploy" sub={source === "ai" ? "Generated by AI from your description." : "Generated by the built-in rule parser."} />

          {/* Coverage warning — logic the builder couldn't express (so it was
              left out). The user should know the strategy is incomplete before
              deploying. */}
          {coverage === "partial" && unsupported.length > 0 && (
            <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", color: "#9a3412", borderRadius: 12, padding: "12px 16px", marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 6 }}>⚠️ Some logic couldn't be built</div>
              <div style={{ fontSize: 12.5, opacity: 0.95, marginBottom: 6 }}>The rest of your idea was built, but these parts aren't supported and were left out:</div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {unsupported.map((u, i) => <li key={i} style={{ fontSize: 12.5, lineHeight: 1.5 }}>{u}</li>)}
              </ul>
              <div style={{ fontSize: 11.5, opacity: 0.85, marginTop: 7 }}>Tip: open it in the Builder to express these with a custom Formula, or refine your prompt.</div>
            </div>
          )}

          {/* How this strategy works — plain-language walkthrough */}
          <div style={{ background: "linear-gradient(135deg,#eef2ff,#f8fafc)", border: "1px solid #c7d2fe", borderRadius: 12, padding: "14px 16px", marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: T.text, marginBottom: 10, display: "flex", alignItems: "center", gap: 7 }}>📖 How this strategy works</div>
            {explainStrategy(strategy).map((sect, i) => (
              <div key={i} style={{ marginBottom: i < 3 ? 9 : 0 }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: T.indigo, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>{sect.h}</div>
                <ul style={{ margin: 0, paddingLeft: 17 }}>
                  {sect.items.map((it, j) => (
                    <li key={j} style={{ fontSize: 12.5, color: "#475569", lineHeight: 1.55, marginBottom: 2, overflowWrap: "anywhere" }}>{it}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Name + lots editable */}
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12, marginBottom: 16 }}>
            <div><Lbl>Strategy Name</Lbl><input style={inp} value={strategy.name || ""} onChange={(e) => setStrategy({ ...strategy, name: e.target.value })} /></div>
            <div><Lbl>Lots</Lbl><input type="number" min={1} style={inp} value={strategy.lots || 1} onChange={(e) => setStrategy({ ...strategy, lots: Math.max(1, +e.target.value || 1) })} /></div>
          </div>

          {/* Meta chips */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
            {[
              ["Instrument", strategy.instrument],
              ["Underlying", strategy.underlyingType],
              ["Product", strategy.productType],
              ["Entry", strategy.entryTime],
              ["Exit", strategy.exitTime],
              ["Type", ind ? "Indicator-based" : "Time-based"],
              ["Days", (strategy.days || []).join(", ")],
            ].filter(([, v]) => v).map(([k, v]) => (
              <span key={k} style={{ fontSize: 11.5, color: T.sub, background: T.field, border: `1px solid ${T.border}`, borderRadius: 8, padding: "5px 10px" }}><b style={{ color: T.text }}>{k}:</b> {v}</span>
            ))}
          </div>

          {/* Legs */}
          <Lbl>Legs</Lbl>
          <div className="ar-tablewrap" style={{ border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden", marginBottom: 16, overflowX: "auto" }}>
            <div className="ar-legsgrid" style={{ minWidth: 420 }}>
              <div style={{ display: "grid", gridTemplateColumns: "0.8fr 0.6fr 1.2fr 0.6fr 0.9fr 0.9fr", gap: 0, background: T.field, fontSize: 10.5, fontWeight: 700, color: T.sub, textTransform: "uppercase", letterSpacing: "0.05em", padding: "8px 12px" }}>
                <span>Action</span><span>Type</span><span>Strike</span><span>Lots</span><span>SL</span><span>Target</span>
              </div>
              {(strategy.legs || []).map((leg, i) => {
                const l = legLabel(leg);
                return (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: "0.8fr 0.6fr 1.2fr 0.6fr 0.9fr 0.9fr", gap: 0, fontSize: 12.5, color: T.text, padding: "9px 12px", borderTop: `1px solid ${T.border}` }}>
                    <span style={{ fontWeight: 700, color: l.action === "SELL" ? T.red : T.green }}>{l.action}</span>
                    <span style={{ fontWeight: 700 }}>{l.cp}</span>
                    <span>{l.strike}</span>
                    <span>{leg.qty || 1}</span>
                    <span>{slText(leg.slType, leg.slValue != null ? leg.slValue : leg.sl)}</span>
                    <span>{slText(leg.tpType, leg.targetValue != null ? leg.targetValue : leg.tp)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Indicator conditions */}
          {ind && (
            <div style={{ marginBottom: 16 }}>
              <Lbl>Entry ({ind.entryLogic} · {ind.timeframe})</Lbl>
              <div style={{ fontSize: 12.5, color: T.text, marginBottom: 8, overflowWrap: "anywhere" }}>{(ind.entryConditions || []).map(condText).join(`  ${ind.entryLogic}  `) || "—"}</div>
              {(ind.exitConditions || []).length > 0 && <>
                <Lbl>Exit ({ind.exitLogic})</Lbl>
                <div style={{ fontSize: 12.5, color: T.text, overflowWrap: "anywhere" }}>{ind.exitConditions.map(condText).join(`  ${ind.exitLogic}  `)}</div>
              </>}
            </div>
          )}

          {/* Overall risk */}
          {(strategy.overallTargetValue || strategy.overallSlValue) ? (
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              {strategy.overallTargetValue ? <span style={{ fontSize: 11.5, color: "#065f46", background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 8, padding: "5px 10px" }}>Overall Target: {strategy.overallTargetType === "amount" ? "₹" : ""}{strategy.overallTargetValue}{strategy.overallTargetType === "percent" ? "%" : ""}</span> : null}
              {strategy.overallSlValue ? <span style={{ fontSize: 11.5, color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "5px 10px" }}>Overall SL: {strategy.overallSlType === "amount" ? "₹" : ""}{strategy.overallSlValue}{strategy.overallSlType === "percent" ? "%" : ""}</span> : null}
            </div>
          ) : null}

          {/* Advanced features */}
          {feats.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <Lbl>Advanced Features</Lbl>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {feats.map((f, i) => <span key={i} style={{ fontSize: 11.5, color: T.indigo, background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: 8, padding: "5px 10px", fontWeight: 600 }}>{f}</span>)}
              </div>
            </div>
          )}

          {/* Deploy controls */}
          <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 16, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div>
              <Lbl>Deploy mode</Lbl>
              <div style={{ display: "inline-flex", background: "#eef1f6", borderRadius: 11, padding: 3, gap: 3 }}>
                {["Paper", "Live"].map((m) => (
                  <button key={m} onClick={() => setMode(m)} style={{ padding: "7px 18px", fontSize: 12.5, fontWeight: 700, borderRadius: 8, border: "none", cursor: "pointer", background: mode === m ? "#fff" : "transparent", color: mode === m ? (m === "Live" ? T.red : T.blue) : T.sub, boxShadow: mode === m ? "0 1px 3px rgba(15,23,42,0.14)" : "none" }}>{m}</button>
                ))}
              </div>
            </div>
            <div style={{ flex: 1 }} />
            <Btn ch="Edit in Builder" onClick={() => submit(false, true)} disabled={busy} />
            <Btn ch="Save Draft" onClick={() => submit(false, false)} disabled={busy} />
            <Btn ch={busy ? "Working…" : (mode === "Live" ? "🧪 Backtest & Approve →" : "🚀 Deploy Paper")} v={mode === "Live" ? "green" : "blue"} onClick={() => submit(true, false)} disabled={busy} />
          </div>
          {mode === "Live"
            ? <div style={{ fontSize: 11.5, color: T.muted, marginTop: 10 }}>🔒 AI strategies can't go Live until backtested &amp; approved. This saves the strategy and opens the backtest — review it there, then Approve &amp; Deploy Live. (Paper mode deploys instantly.)</div>
            : <div style={{ fontSize: 11.5, color: T.muted, marginTop: 10 }}>Paper mode simulates orders with no real money — a safe way to validate before going Live.</div>}
        </Card>
      )}
    </div>
  );
}
